import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { FC, Fragment, useEffect, useState, useRef } from "react";

import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { classNames } from "@/shared/utils/classnames";

type Option = { id: number | string; name: string };

export type MultiSelectProps = {
  options: Option[];
  selectedOptions: Option[] | Option | null;
  setSelectedOptions: (options: Option[] | Option | null) => void;
  title: string;
  textDescribe?: string;
  span?: string;
  textInformation?: string;
  multiple?: boolean;
  className?: string;
};

export const MultiSelect: FC<MultiSelectProps> = ({
  options,
  selectedOptions = [],
  setSelectedOptions,
  title,
  textDescribe,
  span,
  textInformation,
  multiple = true,
  className,
}) => {
  const [isAbove, setIsAbove] = useState(false);
  const listboxRef = useRef<HTMLDivElement>(null);

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    if (!(event.target as HTMLElement).closest(".tooltip-container")) {
      setIsTooltipVisible(false);
    }
  };

  const handleChange = (selectedOption: Option) => {
    if (Array.isArray(selectedOptions) && multiple) {
      const isAlreadySelected = selectedOptions.some(
        (option) => option.id === selectedOption.id
      );

      const updatedOptions = isAlreadySelected
        ? selectedOptions.filter((option) => option.id !== selectedOption.id)
        : [...selectedOptions, selectedOption];

      if (JSON.stringify(updatedOptions) !== JSON.stringify(selectedOptions)) {
        setSelectedOptions(updatedOptions);
      }
    } else {
      setSelectedOptions(selectedOption);
    }
  };

  useEffect(() => {
    const handlePosition = () => {
      if (listboxRef.current) {
        const rect = listboxRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (windowHeight - rect.bottom < 300) {
          setIsAbove(true);
        } else {
          setIsAbove(false);
        }
      }
    };

    window.addEventListener("resize", handlePosition);
    window.addEventListener("scroll", handlePosition);

    handlePosition();

    return () => {
      window.removeEventListener("resize", handlePosition);
      window.removeEventListener("scroll", handlePosition);
    };
  }, []);

  return (
    <Fragment>
      <Listbox
        value={selectedOptions}
        onChange={(option) => {
          handleChange(option);
        }}
      >
        <div ref={listboxRef} className={classNames(className)}>
          <div className="flex justify-between">
            <div className={"flex"}>
              <Label className="block text-sm font-medium leading-6 text-gray-600">
                {title}
              </Label>
              {textInformation && (
                <>
                  {/* Ícono de información con tooltip */}
                  <div className="relative ml-2 tooltip-container">
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="text-gray-500 cursor-pointer"
                      onClick={() => setIsTooltipVisible(!isTooltipVisible)}
                    />

                    {/* Tooltip que aparece al hacer clic en el ícono */}
                    {isTooltipVisible && (
                      <div
                        className={`absolute left-0 text-xs top-full mt-1 w-40 p-2 text-gray-500 bg-white rounded-md shadow-lg z-20 transition-opacity opacity-100`}
                      >
                        {textInformation}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <span className={`text-sm leading-6 text-gray-500`}>{span}</span>
          </div>

          <div className="relative">
            <ListboxButton className="relative min-w-48 w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6">
              <span className="block truncate">
                {Array.isArray(selectedOptions)
                  ? selectedOptions.length > 0
                    ? selectedOptions
                        ?.map((o: any) => o.name ?? o.label)
                        .join(", ")
                    : "Nada Seleccionado"
                  : (selectedOptions?.name ??
                    selectedOptions?.label ??
                    "Nada Seleccionado")}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-gray-300"
                />
              </span>
            </ListboxButton>

            <ListboxOptions
              transition
              className={`absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
                isAbove ? "bottom-full mb-1" : "top-full mt-1"
              }`}
            >
              {options?.length > 0 &&
                options?.map((person) => (
                  <ListboxOption
                    key={person.id}
                    value={person}
                    className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-600 data-[focus]:bg-primary data-[focus]:text-white"
                  >
                    <span className="block truncate font-normal group-data-[selected]:font-semibold">
                      {person.name ?? person.label}
                    </span>

                    {Array.isArray(selectedOptions) &&
                      selectedOptions.some((o: any) => o.id === person.id) && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-hover:text-white">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                  </ListboxOption>
                ))}
            </ListboxOptions>
          </div>
          <p className="mt-1 text-sm text-gray-400">{textDescribe}</p>
        </div>
      </Listbox>
    </Fragment>
  );
};
